import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTonConnectUI, useTonWallet, useTonAddress } from '@tonconnect/ui-react';
import { CopyRow } from './CopyRow';
import {
  refreshTonPaymentRequest,
  shortTonAddress,
  submitTonConnectWallet,
  type TonConnectDeposit,
} from '../api/tonConnect';
import { paymentErrorMessage } from '../api/payments';
import {
  buildGramJettonTransferMessage,
  resolveGramJettonWallet,
} from '../tonconnect/gramJettonTransfer';

type Props = {
  deposit: TonConnectDeposit;
  onUpdated: (next: TonConnectDeposit) => void;
  /** When false, keep panel visible but disable pay (QR/address path remains elsewhere). */
  jettonTransferReady?: boolean;
};

export function TonConnectDepositPanel({ deposit, onUpdated, jettonTransferReady = true }: Props) {
  const { t } = useTranslation(['payments', 'errors']);
  const [tonConnectUI] = useTonConnectUI();
  const wallet = useTonWallet();
  const address = useTonAddress();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const meta = deposit.ton_connect;
  const open = deposit.status === 'awaiting_receipt' || deposit.status === 'created';
  const reviewing = deposit.status === 'pending_review';
  const jettonOk = Boolean(
    jettonTransferReady
    && meta?.transfer_kind === 'jetton'
    && meta.jetton_master
    && meta.amount_units
    && meta.receive_address,
  );

  async function connectWallet() {
    setMessage(null);
    try {
      await tonConnectUI.openModal();
      setMessage(t('payments:tonWalletConnectedHint'));
    } catch (err) {
      setMessage(paymentErrorMessage(err, t('payments:tonWalletConnectFailed')));
    }
  }

  async function disconnectWallet() {
    try {
      await tonConnectUI.disconnect();
      setMessage(t('payments:tonWalletDisconnected'));
    } catch (err) {
      setMessage(paymentErrorMessage(err, t('errors:generic')));
    }
  }

  async function payWithWallet() {
    if (!jettonOk || !meta?.payment_request || !meta.memo || !meta.receive_address || !meta.jetton_master || !meta.amount_units) {
      setMessage(t('payments:tonPaymentMissing'));
      return;
    }
    if (!wallet || !address) {
      setMessage(t('payments:tonConnectWalletFirst'));
      return;
    }
    setBusy(true);
    setMessage(t('payments:tonConfirmInWallet'));
    try {
      let request = meta.payment_request;
      const now = Math.floor(Date.now() / 1000);
      if (!request.validUntil || request.validUntil <= now + 30) {
        const refreshed = await refreshTonPaymentRequest(deposit.public_id);
        onUpdated(refreshed);
        request = refreshed.ton_connect?.payment_request || request;
      }

      const jettonWallet = await resolveGramJettonWallet(address, meta.jetton_master);
      if (!jettonWallet) {
        setMessage(t('payments:tonGramWalletMissing'));
        return;
      }

      const gas = BigInt(meta.gas_nanotons || meta.amount_nanotons || request.gas_nanotons || '100000000');
      const units = BigInt(meta.amount_units);
      const destination = meta.receive_address;
      // Always use server destination/master/amount — never UI overrides.
      if (request.destination && request.destination !== destination) {
        throw new Error('ton_payment_invalid');
      }
      if (request.jetton_master && request.jetton_master !== meta.jetton_master) {
        throw new Error('ton_payment_invalid');
      }

      const messageTx = buildGramJettonTransferMessage({
        jettonWalletAddress: jettonWallet,
        destination,
        responseAddress: address,
        amountUnits: units,
        gasNanotons: gas,
        forwardTonAmount: BigInt(request.forward_ton_amount || '1'),
        comment: meta.memo,
      });

      const result = await tonConnectUI.sendTransaction({
        validUntil: request.validUntil,
        messages: [messageTx],
      });
      if (!result?.boc) {
        setMessage(t('payments:tonPaymentProofMissing'));
        return;
      }
      const updated = await submitTonConnectWallet(deposit.public_id, {
        sender_address: address || undefined,
        boc: result.boc,
      });
      onUpdated(updated);
      setMessage(t('payments:tonPendingAdminReview'));
    } catch (err) {
      const text = paymentErrorMessage(err, t('payments:tonPaymentCancelled'));
      setMessage(text);
    } finally {
      setBusy(false);
    }
  }

  if (!meta) return null;

  return (
    <section className="vbb-ton-connect-panel" aria-label={t('payments:tonConnectTitle')}>
      <div className="vbb-crypto-transfer-heading">
        <h3>{t('payments:tonConnectTitle')}</h3>
        <span className="muted-note">GRAM · TON Connect</span>
      </div>

      <div className="vbb-crypto-transfer-fields">
        <div className="vbb-crypto-transfer-row">
          <div className="vbb-crypto-transfer-body">
            <small>{t('payments:cryptoTransferAmount')}</small>
            <strong>
              {deposit.crypto_amount} GRAM
              {meta.amount_units ? ` · ${meta.amount_units} units` : ''}
            </strong>
          </div>
        </div>
        {meta.jetton_master ? (
          <div className="vbb-crypto-transfer-row">
            <CopyRow label={t('payments:tonJettonMaster')} value={meta.jetton_master} />
          </div>
        ) : null}
        {meta.receive_address ? (
          <div className="vbb-crypto-transfer-row">
            <CopyRow label={t('payments:cryptoTransferAddress')} value={meta.receive_address} />
          </div>
        ) : null}
        {meta.memo ? (
          <div className="vbb-crypto-transfer-row">
            <CopyRow label={t('payments:cryptoMemo')} value={meta.memo} />
          </div>
        ) : null}
        <div className="vbb-crypto-transfer-row">
          <div className="vbb-crypto-transfer-body">
            <small>{t('payments:network')}</small>
            <strong>TON · jetton</strong>
          </div>
        </div>
        <div className="vbb-crypto-transfer-row">
          <div className="vbb-crypto-transfer-body">
            <small>{t('payments:tonWalletStatus')}</small>
            <strong>
              {wallet
                ? t('payments:tonWalletConnected', { address: shortTonAddress(address) })
                : t('payments:tonWalletNotConnected')}
            </strong>
          </div>
        </div>
      </div>

      <div className="vbb-crypto-warnings">
        <div className="vbb-crypto-warning">
          <span aria-hidden="true">⚠</span>
          <p>{t('payments:tonWarnGramJettonOnly')}</p>
        </div>
        <div className="vbb-crypto-warning">
          <span aria-hidden="true">⚠</span>
          <p>{t('payments:tonWarnAdminReview')}</p>
        </div>
      </div>

      {!jettonOk ? (
        <p className="muted-note" role="status">{t('payments:tonGramConnectUnavailable')}</p>
      ) : null}

      {open && !reviewing && jettonOk ? (
        <div className="vbb-ton-actions">
          {!wallet ? (
            <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void connectWallet()}>
              {t('payments:tonConnectWallet')}
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void payWithWallet()}>
                {t('payments:tonPayConfirmGram')}
              </button>
              <button type="button" className="btn btn-ghost btn-block" disabled={busy} onClick={() => void disconnectWallet()}>
                {t('payments:tonDisconnectWallet')}
              </button>
            </>
          )}
        </div>
      ) : null}

      {reviewing || meta.wallet_confirmed ? (
        <p className="muted-note" role="status">{t('payments:tonPendingAdminReview')}</p>
      ) : null}

      {message ? <p className="muted-note" role="status">{message}</p> : null}
    </section>
  );
}
